'use client';

import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { usePathname } from 'next/navigation';

import { virtualPageViewDataLayerEvent } from '@/modules/cart/services/data-layer-events/virtual-page-view';
import { useDocumentTitle } from '@/modules/common/components/DataLayer/useDocumentTitle';
import { useWaitForDataLayerEvent } from '@/modules/common/hooks/useWaitForDataLayerEvent';
import { setLastPageViewPath } from '@/modules/common/state/dataLayerSlice';
import { ContentfulCollection } from '@/modules/product/models/collection.interface';
import { useMeQuery } from '@/modules/profile/gql/__generated/meQuery.generated';

import { getCookie } from '@/utils/cookie';
import { capitalizeString } from '@/utils/utils';

type CollectionVariant = 'editorial' | 'room' | 'designer';

export function useCollectionPageView(collection: ContentfulCollection) {
  const pathname = usePathname();
  const dispatch = useDispatch();
  const anonymousCookie = getCookie('ct_anonymous_id');
  const { data } = useMeQuery(undefined, { enabled: !anonymousCookie });
  const documentTitle = useDocumentTitle();
  const gtmLoaded = useWaitForDataLayerEvent('gtm.load');

  const hasSentVirtualPageView = useRef(false);
  const sentDocumentTitle = useRef('');

  useEffect(() => {
    const isIdentified = !!(data?.me?.customer || anonymousCookie);
    if (
      !isIdentified ||
      !gtmLoaded.eventOccurred ||
      documentTitle === sentDocumentTitle.current ||
      hasSentVirtualPageView.current
    ) {
      return;
    }

    const collectionType: CollectionVariant =
      collection?.collectionType === 'editorial' ? 'editorial'
      : collection?.collectionType === 'designer' ? 'designer'
      : 'room';

    const prevUrl = localStorage.getItem('prevUrl') || null;
    const referrer = document.referrer || prevUrl || null;
    const colors = collection?.colors?.filter((c): c is string => !!c) ?? null;
    const roomType = collection?.roomType ?? null;
    const roomStyle = collection?.roomStyle ?? null;
    const author =
      collectionType === 'editorial' ? (collection?.photographer ?? null)
      : collectionType === 'designer' ? (collection?.designer?.name ?? null)
      : null;

    virtualPageViewDataLayerEvent({
      title: documentTitle,
      pathname,
      referrer,
      content_group: 'Shopping',
      content_group2: `${capitalizeString(collectionType)} collection`,
      content_group3: roomType,
      author,
      colors,
      style: roomStyle,
    });

    localStorage.setItem('ds_collection_type', collectionType);
    if (roomType) {
      localStorage.setItem('ds_collection_roomtype', roomType);
    }
    if (roomStyle) {
      localStorage.setItem('ds_collection_style', roomStyle);
    }
    if (colors && colors.length > 0) {
      localStorage.setItem('ds_collection_colors', JSON.stringify(colors));
    }
    localStorage.setItem('prevUrl', window.location.href);

    hasSentVirtualPageView.current = true;
    sentDocumentTitle.current = documentTitle;
    dispatch(setLastPageViewPath(pathname));
  }, [
    anonymousCookie,
    collection,
    data?.me?.customer,
    dispatch,
    documentTitle,
    gtmLoaded.eventOccurred,
    pathname,
  ]);
}
